// Stateful hook over the tabs sessionStorage layer. Single source of
// truth for the open-tab set + active-tab path. Used by SidebarLayout
// (to wrap navigate calls) and TabsBar (to render).

import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  closeTab as closeTabPure,
  nextActivePath,
  openTab as openTabPure,
  readTabs,
  writeTabs,
  type TabRecord,
} from "@/lib/tabs";
import {
  defaultLabelForPath,
  iconKeyForPath,
} from "@/lib/routeIcons";

const FALLBACK_TAB: TabRecord = {
  path: "/app/dashboard",
  label: "Dashboard",
  iconKey: "dashboard",
};

export function useTabs() {
  const navigate = useNavigate();
  const location = useLocation();
  // Lazy initial state from sessionStorage; ensure dashboard is always
  // in the set so the user can never be "tab-less".
  const [tabs, setTabs] = useState<TabRecord[]>(() => {
    const stored = readTabs();
    return stored.length > 0 ? stored : [FALLBACK_TAB];
  });

  // Persist on every change.
  useEffect(() => {
    writeTabs(tabs);
  }, [tabs]);

  // Auto-open a tab for the current URL when the user lands on a route
  // directly (URL bar paste, refresh, browser back) and no matching tab
  // exists yet. Only fires under /app/* — `/login`, `/forgot-password`,
  // `/reset` are unauth routes outside the admin shell.
  useEffect(() => {
    if (!location.pathname.startsWith("/app/")) return;
    const exists = tabs.some(
      (t) => t.path === location.pathname || location.pathname.startsWith(t.path + "/"),
    );
    if (exists) return;
    setTabs((prev) =>
      openTabPure(prev, {
        path: location.pathname,
        label: defaultLabelForPath(location.pathname),
        iconKey: iconKeyForPath(location.pathname),
      }),
    );
  }, [location.pathname, tabs]);

  /** Open a tab (no-op if already open) AND navigate to it. The label +
   *  iconKey are inferred from the path if not supplied — preferred when
   *  the caller already knows them (e.g. sidebar click), passed
   *  explicitly to avoid the inference round-trip. */
  const openAndNavigate = useCallback(
    (path: string, label?: string, iconKey?: string) => {
      const tab: TabRecord = {
        path,
        label: label ?? defaultLabelForPath(path),
        iconKey: iconKey ?? iconKeyForPath(path),
      };
      setTabs((prev) => openTabPure(prev, tab));
      navigate(path);
    },
    [navigate],
  );

  /** Close a tab. If it was the active tab, navigate to the next one
   *  (right neighbor preferred, else left, else dashboard fallback). */
  const close = useCallback(
    (path: string, currentActivePath: string) => {
      const next = closeTabPure(tabs, path, FALLBACK_TAB);
      const navigateTo = nextActivePath(
        tabs,
        currentActivePath,
        path,
        FALLBACK_TAB.path,
      );
      setTabs(next);
      if (navigateTo) navigate(navigateTo);
    },
    [navigate, tabs],
  );

  return { tabs, openAndNavigate, close };
}
